import React from 'react'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { useLogout, useTranslate } from 'react-admin'
import { useQuery } from 'react-query'
import { Avatar, Link, makeStyles, Typography } from '@material-ui/core'
import NoLayoutContainer from '../../components/layout/NoLayoutContainer/NoLayoutContainer'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import ButtonWithLoader from '../../components/ui/ButtonWithLoader/ButtonWithLoader'
import DefaultPlayerIcon from '../../assets/icons/defaultPlayerIcon'
import getInvitationPreview from '../../Apis/invitations/getInvitationPreview'
import getUserData from '../../Apis/user/getUserData'
import getQueryParameterByName from '../../utils/helpers/getQueryParameterByName'
import localStorageIds from '../../utils/constants/localStorageIds'
import INVITATION_TYPES from '../../utils/constants/invitationTypes'
import invitationLandingStyles from './invitationLanding.styles'

const sameAddress = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase()

// Where an invitation email's link lands. It names one invitation, so the receiver is sent on to
// answer that one: through login if the address has an account, through sign-up if not.
const InvitationLanding = () => {
  const { orgId, invitationId } = useParams()
  const token = getQueryParameterByName('token')
  const history = useHistory()
  const location = useLocation()
  const logout = useLogout()
  const translate = useTranslate()
  const useStyles = makeStyles(invitationLandingStyles)
  const classes = useStyles()
  const isLoggedIn = !!localStorage.getItem(localStorageIds.TOKEN)

  const preview = useQuery(
    ['invitationPreview', invitationId, token],
    () => getInvitationPreview({ orgId, invitationId, token }),
    { retry: false },
  )
  // A token left from an expired session must not bounce the receiver to /login: they may have no
  // account yet, and the reload would drop the invitation. Treat it as logged out and drop the token.
  const me = useQuery(
    ['invitationLandingMe'],
    () => getUserData({ skipLoginRedirect: true, skipErrorNotification: true }),
    {
      enabled: isLoggedIn,
      retry: false,
      onError: () => localStorage.removeItem(localStorageIds.TOKEN),
    },
  )

  const answerPath = { pathname: '/invitationsList', search: `?invitation=${invitationId}` }

  if (preview.isLoading || (isLoggedIn && me.isLoading)) {
    return (
      <NoLayoutContainer>
        <LoaderDots />
      </NoLayoutContainer>
    )
  }

  if (preview.isError) {
    return (
      <NoLayoutContainer>
        <div className={classes.wrapper}>
          <Typography variant='h5'>{translate('ra.invitationLanding.unavailableTitle')}</Typography>
          <Typography>{translate('ra.invitationLanding.unavailable')}</Typography>
          <Link href='/login' variant='body2' className={classes.link}>
            {translate('ra.gridLinks.login')}
          </Link>
        </div>
      </NoLayoutContainer>
    )
  }

  const invitation = preview.data
  const signedInEmail = me.data?.email

  if (signedInEmail && sameAddress(signedInEmail, invitation.receiverEmail)) {
    return <Redirect to={answerPath} />
  }

  const senderName =
    `${invitation.sender.firstName ?? ''} ${invitation.sender.lastName ?? ''}`.trim()
  const message =
    invitation.type === INVITATION_TYPES.MEMBERSHIP
      ? translate('ra.invitationLanding.invitedAs', {
          sender: senderName,
          role: invitation.role.toLowerCase(),
        })
      : translate('ra.invitationLanding.invitedToOwn', { sender: senderName })

  const goToLogin = () =>
    history.push({
      pathname: '/login',
      search: `?email=${encodeURIComponent(invitation.receiverEmail)}`,
      // react-admin's login returns here once it succeeds.
      state: { nextPathname: answerPath.pathname, nextSearch: answerPath.search },
    })

  const goToSignUp = () =>
    history.push({
      pathname: '/signUp',
      search: `?email=${encodeURIComponent(invitation.receiverEmail)}&invitation=${invitationId}&invitationToken=${encodeURIComponent(token)}`,
    })

  const renderAction = () => {
    if (signedInEmail) {
      return (
        <>
          <Typography>
            {translate('ra.invitationLanding.otherAccount', {
              receiver: invitation.receiverEmail,
              current: signedInEmail,
            })}
          </Typography>
          <ButtonWithLoader
            type='button'
            fullWidth
            label={translate('ra.invitationLanding.logOutAndContinue')}
            onClick={() => logout(undefined, `${location.pathname}${location.search}`, false)}
          />
        </>
      )
    }

    return invitation.receiverHasAccount ? (
      <ButtonWithLoader
        type='button'
        fullWidth
        label={translate('ra.invitationLanding.logInToAnswer')}
        onClick={goToLogin}
      />
    ) : (
      <>
        <Typography>
          {translate('ra.invitationLanding.createAccountFor', { email: invitation.receiverEmail })}
        </Typography>
        <ButtonWithLoader
          type='button'
          fullWidth
          label={translate('ra.invitationLanding.createAccount')}
          onClick={goToSignUp}
        />
      </>
    )
  }

  return (
    <NoLayoutContainer>
      <div className={classes.wrapper}>
        <div className={classes.org}>
          {invitation.org.logoUrl ? (
            <Avatar alt='' src={invitation.org.logoUrl} className={classes.orgLogo} />
          ) : (
            <DefaultPlayerIcon />
          )}
          <Typography className={classes.orgName}>{invitation.org.name}</Typography>
        </div>
        <Typography>{message}</Typography>
        {renderAction()}
      </div>
    </NoLayoutContainer>
  )
}

export default InvitationLanding
